import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
  type RegistrationError,
  type Token,
} from '@capacitor/push-notifications';
import { ToastController } from '@ionic/angular/standalone';
import { LogService } from 'shared';

const PENDING_REGISTRATION_KEY = 'pushRegistrationPending';
const DEVICE_TOKEN_KEY = 'pushDeviceToken';

export interface DreamPayNotification {
  type: 'transaction_reminder' | 'marketing';
  title: string;
  body: string;
  deepLink?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private readonly router = inject(Router);
  private readonly toastController = inject(ToastController);
  private readonly logger = inject(LogService);

  private initialized = false;

  /**
   * Initialize push notifications: set up listeners and register if needed.
   * Should be called once on app startup when user is authenticated.
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (!Capacitor.isNativePlatform()) return;

    this.initialized = true;
    await this.addListeners();

    // Check if there's a pending registration from a previous failed attempt
    const { value: pending } = await Preferences.get({ key: PENDING_REGISTRATION_KEY });
    if (pending === 'true') {
      await this.register();
    }
  }

  /**
   * Request permissions, register with FCM, and send token to backend.
   */
  async register(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') {
      this.logger.warn('Push notification permission denied', 'PushNotificationService.register');
      return false;
    }

    await PushNotifications.register();
    return true;
  }

  /**
   * Unregister from push notifications and remove token from backend.
   */
  async unregister(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    const { value: token } = await Preferences.get({ key: DEVICE_TOKEN_KEY });
    if (token) {
      this.unregisterTokenFromBackend(token);
      await Preferences.remove({ key: DEVICE_TOKEN_KEY });
    }

    try {
      await PushNotifications.unregister();
    } catch (e) {
      this.logger.warn(
        'Push unregister failed (expected on web)',
        'PushNotificationService.unregister',
        e,
      );
    }
  }

  /**
   * Check if OS-level permissions are still granted.
   * If revoked, unregister from backend.
   */
  async checkPermissionStatus(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;

    const status = await PushNotifications.checkPermissions();
    if (status.receive !== 'granted') {
      await this.unregister();
      return false;
    }
    return true;
  }

  private async addListeners(): Promise<void> {
    // Token received from FCM after registration
    await PushNotifications.addListener('registration', async (token: Token) => {
      this.logger.info('Push registration token received', 'PushNotificationService');
      await Preferences.set({ key: DEVICE_TOKEN_KEY, value: token.value });
      this.registerTokenWithBackend(token.value);
    });

    // Registration error
    await PushNotifications.addListener('registrationError', (error: RegistrationError) => {
      this.logger.error('Push registration failed', 'PushNotificationService', error);
    });

    // Notification received while app is in foreground
    await PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        this.logger.info('Push notification received in foreground', 'PushNotificationService');
        await this.showForegroundToast(notification.title ?? '', notification.body ?? '');
      },
    );

    // User tapped on a notification
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        this.logger.info('Push notification tapped', 'PushNotificationService');
        const data = action.notification.data as DreamPayNotification | undefined;
        if (data?.deepLink) {
          void this.router.navigateByUrl(data.deepLink);
        }
      },
    );
  }

  private registerTokenWithBackend(_token: string): void {
    const platform = Capacitor.getPlatform() as 'ios' | 'android';
    // TODO: Replace with actual device registration endpoint once API client is regenerated
    // this.devicesService.apiV2DevicesRegisterPost({ token: _token, platform, appVersion: '1.0.0' })
    this.logger.info(
      `Device token ready for backend registration (${platform})`,
      'PushNotificationService',
    );

    // Mark registration as pending until backend confirms
    void Preferences.set({ key: PENDING_REGISTRATION_KEY, value: 'true' });

    // Placeholder: will be implemented when backend endpoint is available
    // On success: Preferences.remove({ key: PENDING_REGISTRATION_KEY })
    // On error: keep pending flag for retry on next app launch
  }

  private unregisterTokenFromBackend(_token: string): void {
    // TODO: Replace with actual device unregistration endpoint once API client is regenerated
    // this.devicesService.apiV2DevicesUnregisterPost({ token: _token })
    this.logger.info('Device token ready for backend unregistration', 'PushNotificationService');
  }

  private async showForegroundToast(title: string, body: string): Promise<void> {
    const toast = await this.toastController.create({
      header: title,
      message: body,
      duration: 4000,
      position: 'top',
      buttons: [{ text: 'OK', role: 'cancel' }],
    });
    await toast.present();
  }
}
